import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import Message from '@/components/Message';
import { MaterialIcon } from '@/components/MaterialIcon';
import { showErrorToast, showSuccessToast } from '../../../job_change_processes/lib/toast';
import useDeleteOrganizationApiKey from '../../api/useDeleteOrganizationApiKey';
import type { OrganizationApiKey } from '../../types/Organization';

type ApiKey = Pick<OrganizationApiKey, 'key_id' | 'key_secret'>;

export const IssueApiKeySection: React.FC<{ onIssue: () => void }> = ({ onIssue }) => {
  return (
    <div className="rounded-sm border-solid border-dark-gray bg-white px-12 py-8">
      <div className="mb-8">
        <h2 className="text-xl font-bold">APIキーの発行</h2>
      </div>
      <div className="text-end">
        <Button onClick={onIssue} size="md">
          APIキーを発行する
        </Button>
      </div>
    </div>
  );
};

const ApiKeyDisplay: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const maskedValue = '*'.repeat(value.length - 3) + value.slice(-3);

  return (
    <div className="flex items-center gap-8">
      <div className="w-32 shrink-0 text-base font-bold text-gray-700">{label}</div>
      <div className="flex flex-1 items-center gap-2">
        <div className="text-sm text-gray-600">{maskedValue}</div>
      </div>
    </div>
  );
};

export const ApiKeyInfoSection: React.FC<{ apiKey: ApiKey; onClose: () => void }> = ({
  apiKey,
  onClose,
}) => {
  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccessToast('クリップボードにコピーしました');
    } catch (error) {
      console.log(error);
      showErrorToast('コピーに失敗しました');
    }
  };

  return (
    <div className="rounded-sm border-solid border-dark-gray bg-white px-12 py-8">
      <h2 className="mb-8 text-xl font-bold">認証情報</h2>
      <Message color="caution" className="mb-4">
        APIキーは再表示できません。必ず今コピーして安全な場所に保管してください。
      </Message>
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">APIキー</label>
          <div className="flex items-center gap-2">
            <div className="relative max-w-[600px] flex-1">
              <Input readOnly value={apiKey.key_id} className="w-full bg-gray-50" />
              <button
                type="button"
                onClick={() => handleCopy(apiKey.key_id)}
                className="absolute top-[9px] right-3 text-gray-400 hover:text-gray-600"
                aria-label="APIキーをコピー"
              >
                <MaterialIcon name="content_copy" className="size-6" />
              </button>
            </div>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">シークレットキー</label>
          <div className="flex items-center gap-2">
            <div className="relative max-w-[600px] flex-1">
              <Input readOnly value={apiKey.key_secret} className="w-full bg-gray-50" />
              <button
                type="button"
                onClick={() => handleCopy(apiKey.key_secret)}
                className="absolute top-[9px] right-3 text-gray-400 hover:text-gray-600"
                aria-label="シークレットキーをコピー"
              >
                <MaterialIcon name="content_copy" className="size-6" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 text-end">
        <Button color="secondary" size="md" onClick={onClose}>
          閉じる
        </Button>
      </div>
    </div>
  );
};

export const ExistingApiKeySection: React.FC<{ apiKey: ApiKey; onDelete: () => void }> = ({
  apiKey,
  onDelete,
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { mutateAsync: deleteOrganizationApiKey } = useDeleteOrganizationApiKey();

  const handleDelete = async () => {
    try {
      await deleteOrganizationApiKey();
      setIsDeleteDialogOpen(false);
      onDelete();
      showSuccessToast('APIキーを削除しました');
    } catch (error) {
      showErrorToast('APIキーの削除に失敗しました');
    }
  };

  return (
    <div className="rounded-sm border-solid border-dark-gray bg-white px-12 py-8">
      <div className="flex flex-col items-center space-y-3">
        <ApiKeyDisplay label="APIキー" value={apiKey.key_id} />
        <ApiKeyDisplay label="シークレットキー" value={apiKey.key_secret} />
      </div>
      <div className="mt-6 text-end">
        <Button
          color="danger-secondary"
          size="md"
          onClick={() => setIsDeleteDialogOpen(true)}
          className="border-1 border-solid border-danger"
        >
          <div className="flex items-center gap-1">
            <MaterialIcon name="delete" className="size-6" />
            APIキーを削除する
          </div>
        </Button>
      </div>
      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setIsDeleteDialogOpen(false);
        }}
      >
        <DialogContent className="w-full max-w-[600px] rounded-sm">
          <DialogTitle className="border-b border-solid border-gray-200 px-6 py-4 text-lg! font-bold!">
            APIキーの削除
          </DialogTitle>
          <div className="p-6">
            <p>本当にAPIキーを削除しますか？</p>
          </div>
          <div className="flex justify-end gap-4 border-t border-solid border-gray-200 px-6 py-4">
            <Button color="secondary" size="md" onClick={() => setIsDeleteDialogOpen(false)}>
              キャンセル
            </Button>
            <Button color="danger" size="md" onClick={handleDelete}>
              <div className="flex items-center gap-2">
                <MaterialIcon name="delete" />
                削除する
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const ApiKeyUsageSection: React.FC = () => {
  return (
    <div className="rounded-sm border-solid border-dark-gray bg-white px-12 py-8">
      <h2 className="text-xl font-bold">APIキーの使用方法</h2>
      <p className="mt-4 text-gray-600">
        APIにアクセスするには、APIキーとシークレットキーが必要です。以下のように&apos;Authorization&apos;ヘッダーに含めてください：
      </p>
      <div className="mt-4 rounded bg-gray-100 p-4">
        <p className="font-mono text-sm">
          &apos;Authorization&apos; {'=>'} &quot;Bearer APIキー:シークレットキー&quot;
        </p>
      </div>
    </div>
  );
};
